import { ReadonlyVec3 } from 'gl-matrix';
import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import { IdField } from '~/components/Fields';
import { LabelText, ValueText } from '~/components/Fields/LabeledValue';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import { DoseMatrix } from '~/domain/patient/doseMatrix';
import { createDoseDetailView } from '~/domain/projections/factories';
import type { DoseProvenance } from '~/domain/projections/views';
import {
    buildDoseDisplayView,
    DoseDisplayView,
} from '~/hooks/dose/doseDisplayView';
import { useDoseDisplaySettings } from '~/hooks/dose/useDoseDisplaySettings';
import { showSensitiveInfoState } from '~/state/atoms/viewerAtoms';
import PropertiesPanel, { PropertiesVariant } from './PropertiesPanel';

type DoseDisplayData = {
    sopUid: string;
    type: string;
    provenance: DoseProvenance;
    doseScale: number;
    summationType?: string;
    doseGridResolution?: ReadonlyVec3;
    planLabel?: string;
    maxDoseGy?: number;
    manufacturer?: string;
    manufacturerModelName?: string;
    seriesDescription?: string;
    doseComment?: string;
    softwareVersions?: string;
    normalizationValueGy?: number;
};

/** Flatten a display view into the panel's field set. */
function toDoseDisplayData(dose: DoseDisplayView): DoseDisplayData {
    return {
        sopUid: dose.sopInstanceUid,
        type: dose.type,
        provenance: dose.provenance,
        doseScale: dose.doseScale,
        summationType: dose.summationType,
        doseGridResolution: dose.doseGridResolutionMm,
        planLabel: dose.planLabel,
        maxDoseGy: dose.displayMaxDoseGy,
        manufacturer: dose.manufacturer,
        manufacturerModelName: dose.manufacturerModelName,
        seriesDescription: dose.seriesDescription,
        doseComment: dose.doseComment,
        softwareVersions: dose.softwareVersions,
        normalizationValueGy: dose.normalizationValueGy,
    };
}

interface DosePropertiesProps {
    dose: DoseMatrix | DoseDisplayView;
    showTitle?: boolean;
    /** Opens the dose's DICOM tags; a hover panel's route to them. */
    onViewDicom?: () => void;
    onDelete?: () => void;
    /** The layout of the list; `compact` by default. */
    variant?: PropertiesVariant;
}

export default function DoseProperties({
    dose,
    showTitle = true,
    onViewDicom,
    onDelete,
    variant,
}: DosePropertiesProps) {
    const intl = useIntl();
    const showSensitiveInfo = useRecoilValue(showSensitiveInfoState);

    // A caller holding a raw entity (the dose lists, the change-dose menu and
    // the export list all do) has no display view to hand, but the dose it
    // names may well be the selected one and may well be rescaled. Look its
    // settings up by DoseId and project the same way the selected-dose path
    // does, so every panel showing a given dose reports the same scale,
    // normalization and maximum.
    const isEntity = dose instanceof DoseMatrix;
    const { settings } = useDoseDisplaySettings(isEntity ? dose.id : null);
    const view = useMemo(
        () =>
            isEntity
                ? buildDoseDisplayView(createDoseDetailView(dose), settings)
                : dose,
        [isEntity, dose, settings]
    );

    const doseDisplayData = useMemo(() => toDoseDisplayData(view), [view]);

    const doseScaleDisplay =
        doseDisplayData.doseScale !== 1
            ? doseDisplayData.doseScale.toFixed(2)
            : null;
    const doseGridResolutionLabel = doseDisplayData.doseGridResolution
        ? `${doseDisplayData.doseGridResolution[0]} x ${doseDisplayData.doseGridResolution[1]} x ${doseDisplayData.doseGridResolution[2]}`
        : null;

    return (
        <PropertiesPanel
            variant={variant}
            showTitle={showTitle}
            onViewDicom={onViewDicom}
            onDelete={onDelete}
        >
            <LabelText>
                <FormattedMessage id="doseType" />
            </LabelText>
            <ValueText
                component="span"
                title={
                    doseDisplayData.provenance === 'predicted'
                        ? undefined
                        : doseDisplayData.type
                }
            >
                {doseDisplayData.provenance === 'predicted' ? (
                    <FormattedMessage id="predicted" />
                ) : (
                    doseDisplayData.type
                )}
            </ValueText>

            {doseDisplayData.summationType && (
                <>
                    <LabelText>
                        <FormattedMessage id="doseSummationType" />
                    </LabelText>
                    <ValueText
                        component="span"
                        title={doseDisplayData.summationType}
                    >
                        {doseDisplayData.summationType}
                    </ValueText>
                </>
            )}

            {doseGridResolutionLabel && (
                <>
                    <LabelText>
                        <FormattedMessage id="doseGridResolutionMm" />
                    </LabelText>
                    <ValueText component="span" title={doseGridResolutionLabel}>
                        {doseGridResolutionLabel}
                    </ValueText>
                </>
            )}

            {doseDisplayData.planLabel && (
                <>
                    <LabelText>
                        <FormattedMessage id="description" />
                    </LabelText>
                    <ValueText
                        component="span"
                        title={
                            showSensitiveInfo
                                ? doseDisplayData.planLabel
                                : undefined
                        }
                    >
                        <SensitiveInfoTypography>
                            {doseDisplayData.planLabel}
                        </SensitiveInfoTypography>
                    </ValueText>
                </>
            )}

            {doseDisplayData.maxDoseGy && (
                <>
                    <LabelText>
                        <FormattedMessage id="maxDose" />
                    </LabelText>
                    <ValueText
                        component="span"
                        title={`${doseDisplayData.maxDoseGy.toFixed(1)} Gy`}
                    >
                        {doseDisplayData.maxDoseGy.toFixed(1)} Gy
                    </ValueText>
                </>
            )}

            {doseDisplayData.manufacturer && (
                <>
                    <LabelText>
                        <FormattedMessage id="manufacturer" />
                    </LabelText>
                    <ValueText
                        component="span"
                        title={doseDisplayData.manufacturer}
                    >
                        {doseDisplayData.manufacturer}
                    </ValueText>
                </>
            )}

            {doseDisplayData.manufacturerModelName && (
                <>
                    <LabelText>
                        <FormattedMessage id="manufacturerModelName" />
                    </LabelText>
                    <ValueText
                        component="span"
                        title={doseDisplayData.manufacturerModelName}
                    >
                        {doseDisplayData.manufacturerModelName}
                    </ValueText>
                </>
            )}

            {doseDisplayData.seriesDescription && (
                <>
                    <LabelText>
                        <FormattedMessage id="seriesDescription" />
                    </LabelText>
                    <ValueText
                        component="span"
                        title={
                            showSensitiveInfo
                                ? doseDisplayData.seriesDescription
                                : undefined
                        }
                    >
                        <SensitiveInfoTypography>
                            {doseDisplayData.seriesDescription}
                        </SensitiveInfoTypography>
                    </ValueText>
                </>
            )}

            {doseDisplayData.doseComment && (
                <>
                    <LabelText>
                        <FormattedMessage id="doseComment" />
                    </LabelText>
                    <ValueText
                        component="span"
                        title={
                            showSensitiveInfo
                                ? doseDisplayData.doseComment
                                : undefined
                        }
                    >
                        <SensitiveInfoTypography>
                            {doseDisplayData.doseComment}
                        </SensitiveInfoTypography>
                    </ValueText>
                </>
            )}

            {doseDisplayData.softwareVersions && (
                <>
                    <LabelText>
                        <FormattedMessage id="softwareVersions" />
                    </LabelText>
                    <ValueText
                        component="span"
                        title={doseDisplayData.softwareVersions}
                    >
                        {doseDisplayData.softwareVersions}
                    </ValueText>
                </>
            )}

            {doseDisplayData.normalizationValueGy != null && (
                <>
                    <LabelText>
                        <FormattedMessage id="normalization" />
                    </LabelText>
                    <ValueText
                        component="span"
                        title={`${doseDisplayData.normalizationValueGy.toFixed(1)} Gy`}
                    >
                        {doseDisplayData.normalizationValueGy.toFixed(1)} Gy
                    </ValueText>
                </>
            )}

            {doseScaleDisplay && (
                <>
                    <LabelText>
                        <FormattedMessage id="doseScaledBy" />
                    </LabelText>
                    <ValueText component="span" title={doseScaleDisplay}>
                        {doseScaleDisplay}
                    </ValueText>
                </>
            )}

            <LabelText>
                {intl.formatMessage({ id: 'sopInstanceUid' })}
            </LabelText>
            <IdField
                id={doseDisplayData.sopUid}
                label={undefined}
                typographyProps={{ fontSize: 11 }}
            />
        </PropertiesPanel>
    );
}
